/**
 * AdjustInvoiceModal — seller-facing modal that adjusts line quantities
 * on an unshipped invoice and issues a credit memo for the delta.
 *
 * Behaviour
 *   • Loads the invoice + linked order (need order to enumerate SKUs)
 *   • Per-line: ordered qty + new qty input + reason chip + per-line
 *     credit calculation
 *   • Live "Original → Adjusted → Credit issued $X" totals strip
 *   • On submit: POST /api/invoices/{iid}/adjust
 *       - If invoice is paid|paying, backend returns 403 stepup_required
 *         → we pop StepUpModal(action="invoice_adjust") and replay with
 *         X-Step-Up-Token header
 *       - Else: green toast + onSuccess() callback
 *
 * Caller pattern:
 *   <AdjustInvoiceModal
 *      open={open} onClose={…}
 *      invoice={inv}
 *      onSuccess={(creditMemo) => { reloadInvoices(); }}
 *   />
 */
import { useEffect, useMemo, useState } from "react";
import { Pencil, X, Plus, Minus, Info, Wallet, Zap, ArrowRight, Loader2, AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import StepUpModal from "./StepUpModal";
import InvoiceExpiryChip from "./InvoiceExpiryChip";
import { SellerChangeRequestModal } from "./FulfillmentChangeRequest";

// Statuses where the buyer is contractually committed — adjustments that
// REDUCE the spec require explicit consent via the CRQ flow.
const ACCEPTED_STATUSES = new Set(["paid", "authorized", "financed", "credit_terms", "overdue"]);

export default function AdjustInvoiceModal({ open, onClose, invoice, onSuccess }) {
  const [order, setOrder]     = useState(null);
  const [reasons, setReasons] = useState([]);
  const [adj, setAdj]         = useState({});       // sku → {qty, reason}
  const [note, setNote]       = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [stepupOpen, setStepupOpen] = useState(false);
  // CRQ flow — opens when the seller hits "Send for buyer approval" on an
  // accepted invoice. Pre-fills items from the current order.
  const [crqOpen, setCrqOpen] = useState(false);

  const isAccepted = invoice && ACCEPTED_STATUSES.has(invoice.status);

  // ── Load order + reasons when opened ───────────────────────────────
  useEffect(() => {
    if (!open || !invoice) return;
    setOrder(null); setAdj({}); setNote(""); setSubmitting(false); setStepupOpen(false);
    (async () => {
      try {
        const [oRes, rRes] = await Promise.all([
          api.get("/orders").then(r => r.data),
          api.get("/credit-memos/reasons").then(r => r.data.reasons || []),
        ]);
        const linked = (oRes || []).find(o => o.id === invoice.order_id);
        setOrder(linked);
        setReasons(rRes);
        if (linked) {
          const seed = {};
          (linked.items || []).forEach(it => {
            const sku = it.product_id || it.sku;
            if (sku) seed[sku] = { qty: it.qty, reason: "" };
          });
          setAdj(seed);
        }
      } catch (e) {
        toast.error(formatErr(e, "Could not load order"));
      }
    })();
  }, [open, invoice]);

  const items = order?.items || [];

  // ── Totals + delta ─────────────────────────────────────────────────
  const originalTotal = useMemo(
    () => items.reduce((s, it) => s + (it.qty || 0) * (it.price || 0), 0),
    [items]
  );
  const adjustedTotal = useMemo(
    () => items.reduce((s, it) => {
      const sku = it.product_id || it.sku;
      const a = adj[sku];
      const q = a ? Number(a.qty) : it.qty;
      return s + q * (it.price || 0);
    }, 0),
    [items, adj]
  );
  const credit = +(originalTotal - adjustedTotal).toFixed(2);
  const hasChanges = items.some(it => {
    const sku = it.product_id || it.sku;
    return adj[sku] && Number(adj[sku].qty) !== it.qty;
  });
  const missingReasons = items.some(it => {
    const sku = it.product_id || it.sku;
    const a = adj[sku];
    return a && Number(a.qty) !== it.qty && !a.reason;
  });
  const hasIncrease = items.some(it => {
    const sku = it.product_id || it.sku;
    return adj[sku] && Number(adj[sku].qty) > it.qty;
  });

  const willMoveMoney = ["paid", "paying"].includes(invoice?.status);

  // ── Submit (with step-up retry) ────────────────────────────────────
  const submit = async (stepUpToken) => {
    if (!hasChanges) return;
    if (hasIncrease) {
      toast.error("Quantity increase isn't supported — create a supplementary invoice instead.");
      return;
    }
    if (missingReasons) {
      toast.error("Pick a reason for every changed line");
      return;
    }
    setSubmitting(true);
    try {
      const lineAdjustments = items
        .map(it => {
          const sku = it.product_id || it.sku;
          const a = adj[sku];
          if (!a) return null;
          const q = Number(a.qty);
          if (q === it.qty) return null;
          return { sku, new_qty: q, reason_code: a.reason || "other" };
        })
        .filter(Boolean);

      const headers = stepUpToken ? { "X-Step-Up-Token": stepUpToken } : undefined;
      const { data } = await api.post(
        `/invoices/${invoice.id}/adjust`,
        { line_adjustments: lineAdjustments, note: note || undefined },
        { headers },
      );
      toast.success(`Credit memo ${data.credit_memo.code} issued · $${credit.toFixed(2)} ${willMoveMoney ? "credited to buyer wallet" : "applied to invoice"}`);
      onSuccess?.(data.credit_memo);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403 && detail?.code === "stepup_required") {
        setStepupOpen(true);
        return;
      }
      toast.error(formatErr(e, "Adjustment failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!invoice) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={v => !v && onClose?.()}>
        <DialogContent className="max-w-3xl" data-testid="adjust-invoice-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-4 h-4" />
              Adjust {invoice.code}
            </DialogTitle>
            <div className="text-[12px] text-foreground/60">
              {invoice.buyer_name} · status <span className="mono">{invoice.status}</span>
              {willMoveMoney && (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] mono uppercase tracking-[0.12em] text-amber-800">
                  <Wallet className="w-3 h-3" /> Money will move
                </span>
              )}
              {/* Phase IV — Surface the validity countdown directly in the
               *  Adjust modal header so the seller doesn't accidentally try
               *  to adjust an invoice that's about to auto-void or has
               *  already voided. */}
              {invoice.expires_at && (
                <span className="ml-2 inline-flex items-center gap-1">
                  <InvoiceExpiryChip invoice={invoice} compact/>
                </span>
              )}
            </div>
          </DialogHeader>

          <div className="rounded-lg bg-secondary/40 border border-border px-3 py-2 flex items-start gap-2 text-[12px] text-foreground/70 leading-relaxed">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Use this when the packed quantity differs from the invoice. Money moves <strong>only</strong> when the invoice is already paid — otherwise we just regenerate it. Once shipped, use the claim flow instead. The credit memo will reference invoice <span className="mono">{invoice.code}</span> on every line, wallet entry, and email.
            </span>
          </div>

          {/* Bilateral-consent gate. When the buyer has already accepted
           *  this invoice (paid OR authorized Net-X / credit-terms), the
           *  seller can't silently mutate the spec. Route them through the
           *  CRQ flow instead — the buyer must accept the proposed change,
           *  decline, or cancel the order. The original Adjust path is
           *  still available for pre-acceptance invoices (sent / outstanding). */}
          {isAccepted && (
            <div className="rounded-lg border-2 border-amber-400/60 bg-amber-50/40 p-3 flex items-start gap-2.5" data-testid="adjust-crq-gate">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-700" />
              <div className="flex-1 min-w-0 text-[12.5px] text-amber-900">
                <div className="font-medium mb-0.5">Buyer has accepted this invoice</div>
                <div className="leading-snug">
                  Any short-ship, SKU substitution, or item change requires the buyer's explicit approval — they paid for a specific spec and you can't silently change it. Use the change-request flow below.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCrqOpen(true)}
                className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-medium px-3.5 py-1.5 rounded-full bg-amber-700 text-white hover:bg-amber-800"
                data-testid="adjust-open-crq"
              >
                Send change request →
              </button>
            </div>
          )}

          {!order ? (
            <div className="py-12 flex items-center justify-center text-foreground/50">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading lines…
            </div>
          ) : (
            <>
              <div className="max-h-[42vh] overflow-y-auto pr-1">
                {items.map((line, idx) => {
                  const sku = line.product_id || line.sku;
                  const a = adj[sku] || { qty: line.qty, reason: "" };
                  const q = Number(a.qty);
                  const lineDelta = +((line.qty - q) * (line.price || 0)).toFixed(2);
                  const changed = q !== line.qty;
                  const tooHigh = q > line.qty;
                  return (
                    <div key={sku || idx}
                      data-testid={`adjust-line-${sku}`}
                      className={`grid grid-cols-[1fr_120px_180px_100px] gap-3 py-3 border-t border-border first:border-t-0 items-start ${changed ? "bg-amber-50/40 -mx-3 px-3 rounded-md" : ""}`}>
                      <div>
                        <div className="text-[13px] truncate" title={line.name}>{line.name}</div>
                        <div className="text-[11px] mono text-foreground/50 mt-0.5">
                          {sku} · ${Number(line.price || 0).toFixed(2)}/u
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/50">Ordered</div>
                        <div className="mono text-[14px] mt-1">{line.qty}</div>
                      </div>
                      <div>
                        <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/50">New qty</div>
                        <div className="flex items-center gap-1 mt-1">
                          <button type="button"
                            onClick={() => setAdj(s => ({ ...s, [sku]: { ...a, qty: Math.max(0, q - 1) }}))}
                            data-testid={`adjust-line-dec-${sku}`}
                            className="w-7 h-7 rounded-md border border-border bg-white hover:bg-secondary/40 flex items-center justify-center">
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <Input type="number" min="0" max={line.qty}
                            value={q}
                            onChange={e => setAdj(s => ({ ...s, [sku]: { ...a, qty: Math.max(0, Number(e.target.value) || 0) }}))}
                            className="w-14 h-7 text-center mono text-[13px] px-1"
                            data-testid={`adjust-line-input-${sku}`}
                          />
                          <button type="button"
                            onClick={() => setAdj(s => ({ ...s, [sku]: { ...a, qty: Math.min(line.qty, q + 1) }}))}
                            data-testid={`adjust-line-inc-${sku}`}
                            className="w-7 h-7 rounded-md border border-border bg-white hover:bg-secondary/40 flex items-center justify-center">
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {changed && !tooHigh && (
                          <select
                            value={a.reason}
                            onChange={e => setAdj(s => ({ ...s, [sku]: { ...a, reason: e.target.value }}))}
                            data-testid={`adjust-line-reason-${sku}`}
                            className="mt-2 w-full h-8 text-[12px] rounded-md border border-border bg-white px-2">
                            <option value="">Pick a reason…</option>
                            {reasons.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                          </select>
                        )}
                        {tooHigh && (
                          <div className="mt-2 text-[11px] text-rose-700">
                            Use a supplementary invoice for increases.
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/50">Credit</div>
                        <div className={`mono text-[14px] mt-1 ${lineDelta > 0 ? "text-emerald-600" : "text-foreground/40"}`}>
                          {lineDelta > 0 ? `+$${lineDelta.toFixed(2)}` : "—"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Totals strip */}
              <div className="rounded-xl border border-border bg-secondary/30 p-4 grid grid-cols-3 gap-4 items-center">
                <div>
                  <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/50">Original</div>
                  <div className="mono text-[15px] mt-0.5">${originalTotal.toFixed(2)}</div>
                </div>
                <div className="text-center">
                  <ArrowRight className="w-4 h-4 mx-auto text-foreground/40" />
                </div>
                <div className="text-right">
                  <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/50">Adjusted</div>
                  <div className="mono text-[15px] mt-0.5">${adjustedTotal.toFixed(2)}</div>
                </div>
                {hasChanges && credit > 0 && (
                  <div className="col-span-3 mt-3 pt-3 border-t border-border flex items-center justify-between">
                    <div className="text-[12px] text-foreground/65 flex items-center gap-2">
                      <Wallet className="w-4 h-4" />
                      {willMoveMoney
                        ? `Credit to buyer wallet · referencing ${invoice.code}`
                        : `Invoice ${invoice.code} balance reduced (no money moves until paid)`}
                    </div>
                    <div className="mono text-[16px] text-emerald-600" data-testid="adjust-credit-amount">
                      ${credit.toFixed(2)}
                    </div>
                  </div>
                )}
              </div>

              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Optional internal note (visible to buyer)"
                maxLength={2000}
                rows={2}
                data-testid="adjust-invoice-note"
                className="w-full text-[12px] rounded-md border border-border bg-white p-2 resize-none"
              />

              <div className="flex items-center justify-between">
                <button onClick={onClose} className="text-[12px] mono uppercase tracking-[0.14em] text-foreground/55 hover:text-foreground" data-testid="adjust-invoice-cancel">
                  Cancel
                </button>
                <Button
                  onClick={() => submit(null)}
                  disabled={!hasChanges || hasIncrease || missingReasons || submitting || credit <= 0}
                  data-testid="adjust-invoice-confirm"
                  className="rounded-full bg-foreground text-background hover:bg-foreground/90">
                  {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
                  {hasChanges && credit > 0
                    ? `Confirm · issue $${credit.toFixed(2)} credit`
                    : hasChanges && credit <= 0
                    ? "No credit to issue"
                    : "No changes"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <StepUpModal
        open={stepupOpen}
        onClose={() => setStepupOpen(false)}
        action="invoice_adjust"
        amount={credit}
        title={`Confirm $${credit.toFixed(2)} credit on ${invoice.code}`}
        description="We'll text a 6-digit code to your verified phone before moving funds."
        onVerified={(token) => {
          setStepupOpen(false);
          submit(token);
        }}
      />

      {/* Bilateral-consent CRQ flow — opens from the amber gate above
       *  when the seller wants to change an already-accepted invoice. */}
      <SellerChangeRequestModal
        open={crqOpen}
        onClose={() => setCrqOpen(false)}
        order={order}
        invoice={invoice}
        onSent={() => {
          setCrqOpen(false);
          onClose?.();
          onSuccess?.();
        }}
      />
    </>
  );
}
